import { useRef, useState } from 'react';
import { App as AntApp, Form, Input, Button, Radio, Checkbox, Alert } from 'antd';
import {
  ExclamationCircleOutlined,
  IdcardOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { patchSignupState, clearSignupState } from '../../contexts/signupStore';
import { isGstin, isMobileIN, isEmail, digitsOnly, gstinInput } from '../../utils/validators';
import {
  sanitizeEmail,
  sanitizeGstin,
  sanitizeMobile,
  sanitizeText
} from '../../utils/sanitize';
import { notifyError, notifySuccess } from '../../utils/notify';
import { getGstinErrorConfig } from '../../utils/gstinErrorMessages';
import {
  supportConfig,
  supportMailtoHref,
  supportTelHref
} from '../../config/support';
import ShieldDocIllustration from '../../components/onboarding/ShieldDocIllustration';

// Render the time-remaining string used in the SIGNUP_IN_PROGRESS
// modal. Keep the copy plain English — never expose the raw
// timeRemainingSeconds value to the user.
function formatTimeRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'It will expire shortly.';
  }
  const minutes = Math.round(seconds / 60);
  if (minutes >= 120) {
    const hours = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return `It will expire in about ${hours} hours.`;
    return `It will expire in about ${hours} hours ${m} minute${m === 1 ? '' : 's'}.`;
  }
  if (minutes >= 60) {
    const m = minutes - 60;
    if (m === 0) return 'It will expire in about 1 hour.';
    return `It will expire in about 1 hour ${m} minute${m === 1 ? '' : 's'}.`;
  }
  if (minutes >= 2) return `It will expire in about ${minutes} minutes.`;
  if (minutes === 1) return 'It will expire in about 1 minute.';
  return 'It will expire in less than a minute.';
}

// Support-contact line shown inside the SIGNUP_IN_PROGRESS modal.
// Email + mobile come from VITE_SUPPORT_EMAIL / VITE_SUPPORT_MOBILE
// via src/config/support.js (with safe fallbacks). Rendered with
// mailto: / tel: anchors so users can tap-to-act on mobile.
function SupportLine() {
  return (
    <p className="m-0 mt-3 text-ink-500 text-sm">
      Need help? Contact support at{' '}
      <a
        href={supportMailtoHref()}
        className="text-brand-500 hover:text-brand-600 font-medium"
      >
        {supportConfig.email}
      </a>{' '}
      or{' '}
      <a
        href={supportTelHref()}
        className="text-brand-500 hover:text-brand-600 font-medium whitespace-nowrap"
      >
        {supportConfig.mobile}
      </a>
      .
    </p>
  );
}

export default function SignupStartPage() {
  const { modal } = AntApp.useApp();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  // Stage 5N-13.1 UX: keep a ref to the GSTIN <Input> so we can focus
  // it after a format-invalid / not-found modal closes — the user's
  // next action is always to fix the number.
  const gstinInputRef = useRef(null);
  const focusGstinField = () => {
    try {
      // Ant Design's Input forwards .focus() to the underlying input.
      if (gstinInputRef.current && typeof gstinInputRef.current.focus === 'function') {
        gstinInputRef.current.focus({ cursor: 'all' });
      }
    } catch (_e) { /* noop — focus is best-effort */ }
  };

  async function onFinish(values) {
    setSubmitting(true);
    // Build body OUTSIDE the try block so the SIGNUP_IN_PROGRESS catch
    // can prefill signupStore with the same contact details when the
    // user picks "Continue existing signup". Each field is normalised
    // through the centralised sanitisers (uppercase GSTIN, lowercase
    // email, digits-only 10-char mobile, control-char-stripped name)
    // so the payload sent to /api/signup/start is canonical regardless
    // of how the user pasted/typed it.
    // Stage 5N-10: tax category fields. Defaults map to the existing
    // regular-customer flow (customerTaxType='regular', no SEZ
    // declaration). When the user picks SEZ Unit / Developer, the
    // backend service validates that BOTH the LOA / approval number
    // and the authorised-operations checkbox are present and rejects
    // with SEZ_DECLARATION_REQUIRED / SEZ_APPROVAL_NUMBER_REQUIRED if
    // not.
    const taxType = values.customerTaxType || 'regular';
    const isSez = taxType === 'sez_unit' || taxType === 'sez_developer';
    const body = {
      gstin: sanitizeGstin(values.gstin),
      contactName: sanitizeText(values.contactName, { collapseWhitespace: true, maxLength: 120 }),
      contactEmail: sanitizeEmail(values.contactEmail),
      contactMobile: sanitizeMobile(values.contactMobile),
      customerTaxType: taxType,
      sezApprovalNumber: isSez
        ? sanitizeText(values.sezApprovalNumber || '', { collapseWhitespace: true, maxLength: 120 })
        : null,
      sezAuthorisedOperationsConfirmed: isSez
        ? Boolean(values.sezAuthorisedOperationsConfirmed)
        : false
    };
    try {
      // A new signup starts a fresh wizard.
      clearSignupState();
      const data = await endpoints.signup.start(body);
      patchSignupState({
        pendingSignupId: data.pendingSignupId,
        gstin: data.gstin,
        verification: data.verification,
        contactName: body.contactName,
        contactEmail: body.contactEmail,
        contactMobile: body.contactMobile,
        startedAt: new Date().toISOString()
      });
      // Single success toast then move straight into the OTP step.
      // No interim "GSTIN verified" panel — the legal/trade name is
      // already persisted on the pending signup and surfaces later in
      // the flow (and on the internal-admin pending-approval view).
      notifySuccess('GSTIN verified successfully');
      navigate('/signup/otp');
    } catch (err) {
      // SIGNUP_IN_PROGRESS is a blocking, action-required state. The
      // backend now ships safe recovery details (pendingSignupId,
      // currentStep, expiresAt, timeRemainingSeconds, canResume) so
      // the user can either resume their existing signup or wait it
      // out. Anything else -> generic toast-style error popup.
      const errResp = err && err.response && err.response.data && err.response.data.error;
      const code = errResp && errResp.code;
      if (code === 'SIGNUP_IN_PROGRESS') {
        const details = (errResp && errResp.details) || {};
        const timePhrase = formatTimeRemaining(details.timeRemainingSeconds);
        const description = `A signup is already active for this GSTIN or email. ${timePhrase}`;

        if (details.canResume && details.pendingSignupId) {
          modal.confirm({
            title: 'Signup already in progress',
            icon: (
              <ExclamationCircleOutlined style={{ color: '#F59E0B' }} />
            ),
            content: (
              <div>
                <p className="m-0">{description}</p>
                <p className="m-0 mt-2 text-ink-500 text-sm">
                  You can continue from where you left off, or try again
                  after it expires.
                </p>
                <SupportLine />
              </div>
            ),
            okText: 'Continue existing signup',
            cancelText: "I'll try later",
            okButtonProps: { type: 'primary' },
            maskClosable: false,
            onOk: () => {
              // Restore the routing-id so the resumed page can fetch
              // its own status. We re-save the contact details the
              // user just typed too — they're identical to the
              // existing pending row's masked destinations and let
              // any prefill logic on the OTP / payment page work
              // without an extra round-trip.
              patchSignupState({
                pendingSignupId: details.pendingSignupId,
                gstin: body.gstin,
                contactName: body.contactName,
                contactEmail: body.contactEmail,
                contactMobile: body.contactMobile,
                resumedAt: new Date().toISOString()
              });
              const next =
                details.currentStep === 'payment'
                  ? '/signup/payment'
                  : '/signup/otp';
              navigate(next);
            }
          });
        } else {
          // canResume=false: too little time left to be worth resuming.
          // Single-button modal.warning with the same support-contact
          // line so the user has a path forward beyond just waiting.
          modal.warning({
            title: 'Signup already in progress',
            content: (
              <div>
                <p className="m-0">
                  A signup is already active for this GSTIN or email and
                  will expire shortly. Please wait until it expires or
                  contact support.
                </p>
                <SupportLine />
              </div>
            ),
            okText: 'OK',
            maskClosable: true
          });
        }
      } else {
        // Stage 5N-13.1 UX — every GSTIN-bucket error (format invalid,
        // not found, inactive, provider unavailable, timeout, legal-name
        // mismatch, generic verification failure, browser network /
        // timeout) routes through gstinErrorMessages.js so the modal
        // copy is consistent and professional. SIGNUP_IN_PROGRESS is
        // already handled above with its own action-required modal.
        // Anything that isn't a known GSTIN bucket OR a network/timeout
        // error falls back to the centralised notifyError pipeline
        // (e.g. SEZ_DECLARATION_REQUIRED, RATE_LIMITED, server 5xx).
        const gstinCfg = getGstinErrorConfig(err);
        if (gstinCfg.matched) {
          const popup = gstinCfg.severity === 'warning'
            ? modal.warning
            : modal.error;
          popup({
            title: gstinCfg.title,
            content: (
              <div>
                <p className="m-0">{gstinCfg.message}</p>
                {gstinCfg.subtext ? (
                  <p className="m-0 mt-2 text-ink-500 text-sm">{gstinCfg.subtext}</p>
                ) : null}
              </div>
            ),
            okText: gstinCfg.okText,
            maskClosable: true,
            onOk: () => {
              if (gstinCfg.focusGstin) focusGstinField();
            },
            // Cover the masked-close + Esc paths too so the focus
            // restoration is consistent regardless of how the user
            // dismisses the modal.
            afterClose: () => {
              if (gstinCfg.focusGstin) focusGstinField();
            }
          });
        } else {
          notifyError(err, { title: 'Could not start signup' });
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="gi-onboarding-rise bg-white rounded-2xl shadow-elevated border border-surface-200 p-5 sm:p-7 lg:p-8">
      <div className="mt-5 mb-4 flex items-start gap-4 min-w-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight">
            Create your account
          </h2>
          <p className="text-sm sm:text-base text-ink-500 mt-1.5 mb-0 max-w-md">
            Powering India&rsquo;s glass businesses with digital precision
          </p>
        </div>
        <div className="hidden xl:block w-28 2xl:w-32 shrink-0 -mt-1 ml-auto">
          <ShieldDocIllustration className="w-full h-auto" />
        </div>
      </div>

      <Form
        layout="vertical"
        onFinish={onFinish}
        disabled={submitting}
        size="large"
        className="onboarding-compact-form"
        initialValues={{
          customerTaxType: 'regular',
          sezAuthorisedOperationsConfirmed: false
        }}
        requiredMark={(label, info) => (
          <span className="text-sm font-semibold text-ink-800 inline-flex items-center gap-1">
            {info.required ? (
              <span className="text-rose-500" aria-hidden="true">
                *
              </span>
            ) : null}
            {label}
          </span>
        )}
      >
        <Form.Item
          label="GSTIN"
          tooltip={{
            title: '15-character GSTIN issued by the GST department.',
            icon: <InfoCircleOutlined />
          }}
          name="gstin"
          getValueFromEvent={gstinInput}
          extra={
            <span className="text-xs text-ink-500">
              Enter your 15-digit GSTIN (e.g. 27AAAPL1234C1ZV)
            </span>
          }
          rules={[
            { required: true, message: 'GSTIN is required' },
            {
              validator: (_r, v) =>
                !v || isGstin(v.toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error('15-char GSTIN expected'))
            }
          ]}
        >
          <Input
            ref={gstinInputRef}
            prefix={<IdcardOutlined className="text-brand-500" />}
            placeholder="27AAAPL1234C1ZV"
            maxLength={15}
            autoCapitalize="characters"
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item
          label="Contact name"
          name="contactName"
          extra={
            <span className="text-xs text-ink-500">
              Person we can reach out to regarding this account
            </span>
          }
          rules={[{ required: true, message: 'name is required' }]}
        >
          <Input
            prefix={<UserOutlined className="text-brand-500" />}
            placeholder="Ravi Kumar"
          />
        </Form.Item>

        {/* Email + mobile side-by-side at sm+ to keep the form compact;
            stacks on narrow widths. The two Form.Items share their own
            row so each contributes only one Form.Item margin-bottom
            instead of two stacked vertical gaps. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">
          <Form.Item
            label="Contact email"
            name="contactEmail"
            extra={
              <span className="text-xs text-ink-500">
                We&apos;ll send important updates here
              </span>
            }
            rules={[
              { required: true, message: 'email is required' },
              {
                validator: (_r, v) =>
                  !v || isEmail(v)
                    ? Promise.resolve()
                    : Promise.reject(new Error('valid email expected'))
              }
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-brand-500" />}
              placeholder="ravi@example.com"
            />
          </Form.Item>

          <Form.Item
            label="Contact mobile (10 digits)"
            name="contactMobile"
            getValueFromEvent={digitsOnly(10)}
            extra={
              <span className="text-xs text-ink-500">
                We&apos;ll send OTPs to this number
              </span>
            }
            rules={[
              { required: true, message: 'mobile is required' },
              {
                validator: (_r, v) =>
                  !v || isMobileIN(v)
                    ? Promise.resolve()
                    : Promise.reject(new Error('10-digit Indian mobile expected'))
              }
            ]}
          >
            <Input
              prefix={<PhoneOutlined className="text-brand-500" />}
              placeholder="9876543210"
              maxLength={10}
              inputMode="numeric"
              autoComplete="tel"
            />
          </Form.Item>
        </div>

        {/* Stage 5N-10: customer tax category. Defaults to Regular —
            the SEZ block is only revealed when the user picks SEZ
            Unit / Developer. SEZ zero-rated GST is NOT applied
            instantly — internal admin must approve the declaration
            first, and only future renewal invoices billed AFTER
            approval are zero-rated. The initial signup invoice always
            bills regular taxable GST in this phase. */}
        <Form.Item
          label="Customer GST category"
          name="customerTaxType"
          extra={
            <span className="text-xs text-ink-500">
              SEZ zero-rated billing is subject to internal verification and approval.
            </span>
          }
        >
          <Radio.Group>
            <Radio value="regular">Regular GST customer</Radio>
            <Radio value="sez_unit">SEZ Unit</Radio>
            <Radio value="sez_developer">SEZ Developer</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, next) => prev.customerTaxType !== next.customerTaxType}
        >
          {({ getFieldValue }) => {
            const selected = getFieldValue('customerTaxType') || 'regular';
            const isSez = selected === 'sez_unit' || selected === 'sez_developer';
            if (!isSez) return null;
            return (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                <Alert
                  type="info"
                  showIcon
                  className="mb-3"
                  message="SEZ approval required"
                  description={
                    'Your initial subscription will be billed with regular GST. ' +
                    'After our team verifies your SEZ status, future renewal ' +
                    'invoices will be issued as zero-rated supplies.'
                  }
                />
                <Form.Item
                  label="SEZ approval / LOA number"
                  name="sezApprovalNumber"
                  extra={
                    <span className="text-xs text-ink-500">
                      LOA / approval number issued to your SEZ unit or developer.
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: 'SEZ approval / LOA number is required'
                    },
                    { max: 120, message: 'maximum 120 characters' }
                  ]}
                >
                  <Input
                    prefix={<IdcardOutlined className="text-amber-600" />}
                    placeholder="e.g. KASEZ/IT/L-2/2018-19/12345"
                    maxLength={120}
                  />
                </Form.Item>
                <Form.Item
                  name="sezAuthorisedOperationsConfirmed"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_r, v) =>
                        v
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                'You must confirm the authorised-operations declaration to continue as SEZ.'
                              )
                            )
                    }
                  ]}
                >
                  <Checkbox>
                    I confirm this subscription is procured for authorised
                    operations of the SEZ unit / developer.
                  </Checkbox>
                </Form.Item>
              </div>
            );
          }}
        </Form.Item>

        <div className="mt-1 mb-4 rounded-xl border border-surface-200 bg-surface-50 px-3.5 py-2.5 flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
            <SafetyOutlined />
          </span>
          <p className="text-sm text-ink-700 m-0">
            Your information is secure and used only for verification
            purposes.
          </p>
        </div>

        <Form.Item className="!mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-3">
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SafetyCertificateOutlined />}
              className="!h-12 !text-base !font-semibold !px-5 w-full sm:w-auto"
            >
              Verify GSTIN &amp; Continue
              <ArrowRightOutlined />
            </Button>
            <Link
              to="/login"
              className="text-sm font-semibold text-brand-500 hover:text-brand-600 transition-colors text-center sm:text-left"
            >
              I already have an account →
            </Link>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
